import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Select } from "../../shared/ui";
import type { EmployeeSummary, PageResponse, WorkItemDetail } from "../../shared/types/api";

/**
 * Changes the single accountable owner, and manages contributors (§21).
 *
 * <p>The two are on one panel because the distinction between them is the point, and it is
 * clearest when they sit side by side: one person is answerable, everyone else is helping. §21
 * asks that work with two genuinely accountable owners be split into two subtasks instead, and
 * the shape of this panel makes that the obvious move rather than a rule to remember.
 */
export function AssignDrawer({
  item,
  onClose,
  onSaved,
}: {
  item: WorkItemDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [assigneeId, setAssigneeId] = useState("");
  const [contributorId, setContributorId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: Boolean(item),
  });

  useEffect(() => {
    if (item) {
      setAssigneeId(item.assigneeId ?? "");
      setContributorId("");
      setError(null);
    }
  }, [item]);

  const saveAssignee = async () => {
    if (!item) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/work-items/${item.id}/assignee`, {
        assigneeId: assigneeId || null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not reassign this.");
    } finally {
      setSaving(false);
    }
  };

  const addContributor = async () => {
    if (!item || !contributorId) {
      return;
    }
    setError(null);
    try {
      await api.post(`/api/v1/work-items/${item.id}/contributors`, {
        employeeId: contributorId,
      });
      setContributorId("");
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not add that person.");
    }
  };

  const removeContributor = async (employeeId: string) => {
    if (!item) {
      return;
    }
    setError(null);
    try {
      await api.del(`/api/v1/work-items/${item.id}/contributors/${employeeId}`);
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not remove that person.");
    }
  };

  const people = employees.data?.content ?? [];

  return (
    <Drawer
      open={Boolean(item)}
      onClose={onClose}
      title="People on this item"
      subtitle={item ? `${item.itemKey} — ${item.title}` : undefined}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => void saveAssignee()} loading={saving}>
            {saving ? "Saving…" : "Save assignee"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field
          label="Assignee"
          hint="One person, accountable for this item finishing."
        >
          <Select value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
            <option value="">Unassigned</option>
            {people.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName} — {employee.designation}
              </option>
            ))}
          </Select>
        </Field>

        <InlineAlert tone="info">
          There is deliberately room for one. Where two people are genuinely responsible for
          separate halves of this, §21 asks for two subtasks — otherwise "who is answerable" stops
          having an answer.
        </InlineAlert>

        <section className="border-t border-ink-100 pt-4">
          <h3 className="text-sm font-semibold text-ink-900">Contributors</h3>
          <p className="mb-3 mt-0.5 text-sm text-ink-500">
            The people helping. As many as it takes — they are not a second owner.
          </p>

          {item && item.contributors.length > 0 && (
            <ul className="mb-3 overflow-hidden rounded-lg border border-ink-200">
              {item.contributors.map((person) => (
                <li
                  key={person.id}
                  className="flex items-center justify-between gap-3 border-b border-ink-100 px-3 py-2 text-sm last:border-0"
                >
                  <span className="text-ink-800">{person.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-ink-400 hover:text-red-600"
                    onClick={() => void removeContributor(person.id)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field label="Add someone">
                <Select
                  value={contributorId}
                  onChange={(event) => setContributorId(event.target.value)}
                >
                  <option value="">Choose a person</option>
                  {people
                    .filter((employee) => employee.id !== assigneeId)
                    .filter(
                      (employee) =>
                        !item?.contributors.some((person) => person.id === employee.id),
                    )
                    .map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.fullName}
                      </option>
                    ))}
                </Select>
              </Field>
            </div>
            <Button
              variant="secondary"
              disabled={!contributorId}
              onClick={() => void addContributor()}
            >
              Add
            </Button>
          </div>
        </section>
      </div>
    </Drawer>
  );
}
